import React, { useState } from "react";
import { trackError } from "../../../../lib/metrics";

const CORRECT_CODE = "272";

export default function TerminalUnlock({ onSuccess, alreadyUnlocked, sessionCode, stage = 2, playerId }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);
  const [status, setStatus] = useState(alreadyUnlocked ? "success" : "idle");

  if (alreadyUnlocked) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] py-12 px-4 bg-gradient-to-br from-slate-950 via-indigo-950/20 to-slate-950">
        <div className="w-full max-w-lg space-y-6">
          <div className="text-center space-y-4">
            <div className="w-24 h-24 mx-auto bg-emerald-500/10 border-2 border-emerald-500/30 rounded-2xl flex items-center justify-center">
              <svg className="w-12 h-12 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="text-xl font-semibold text-emerald-300">Terminal déverrouillé</div>
              <div className="text-sm text-slate-400 mt-2">Accès autorisé — ID Badge validé</div>
            </div>
          </div>
          <div className="bg-slate-900/60 border border-emerald-700/50 rounded-lg p-4 space-y-2 text-xs">
            <div className="flex justify-between"><span className="text-slate-500">Système</span><span className="text-slate-300 font-mono">ORIGAMI Terminal v2.1</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Statut</span><span className="text-emerald-400 font-mono">DÉVERROUILLÉ</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Utilisateur</span><span className="text-indigo-400 font-mono">ID Badge #272</span></div>
          </div>
          <button onClick={onSuccess} className="w-full px-4 py-3 rounded-lg font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition">
            Accéder au fragment
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanInput = code.trim();
    setStatus("checking");

    setTimeout(() => {
      if (cleanInput === CORRECT_CODE) {
        setStatus("success");
        setTimeout(() => { onSuccess?.(); }, 1500);
      } else {
        setError(true);
        setStatus("idle");
        trackError({ sessionCode, stage, playerId, reason: "s2.p5.terminal.wrong_id", meta: { attempt: cleanInput } });
        setTimeout(() => setError(false), 2000);
      }
    }, 800);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] py-12 px-4 bg-gradient-to-br from-slate-950 via-indigo-950/20 to-slate-950">
      <div className="w-full max-w-lg space-y-6">
        <div className="text-center space-y-4">
          <div className="w-24 h-24 mx-auto bg-indigo-500/10 border-2 border-indigo-500/30 rounded-2xl flex items-center justify-center">
            <svg className="w-12 h-12 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <div className="text-xl font-semibold text-slate-200">Terminal sécurisé</div>
            <div className="text-sm text-slate-400 mt-2">Accès restreint — Badge employé requis</div>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-700/50 rounded-lg p-4 space-y-2 text-xs">
          <div className="flex justify-between"><span className="text-slate-500">Système</span><span className="text-slate-300 font-mono">ORIGAMI Terminal v2.1</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Emplacement</span><span className="text-slate-300 font-mono">Salle des Plannings</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Statut</span><span className="text-amber-400 font-mono">VERROUILLÉ</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Authentification</span><span className="text-indigo-400 font-mono">ID Badge</span></div>
        </div>

        <form onSubmit={handleSubmit} className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-6 space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">
              ID Badge employé
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="• • •"
              disabled={status === "success"}
              maxLength={3}
              className={`
                w-full px-4 py-3 rounded-lg text-center
                bg-black/60 border-2
                ${error ? "border-red-500 text-red-400" : "border-slate-600 text-slate-200"}
                ${status === "success" ? "border-emerald-500 text-emerald-300" : ""}
                font-mono text-2xl tracking-widest
                focus:outline-none focus:border-indigo-500
                transition-colors disabled:opacity-50 disabled:cursor-not-allowed
              `}
            />
            {error && (
              <div className="mt-2 text-xs text-red-400 flex items-center gap-2">
                <span>❌</span><span>ID Badge invalide</span>
              </div>
            )}
            {status === "success" && (
              <div className="mt-2 text-xs text-emerald-400 flex items-center gap-2">
                <span>✓</span><span>Accès autorisé — Déverrouillage...</span>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={status === "checking" || status === "success" || code.length !== 3}
            className={`
              w-full px-4 py-3 rounded-lg font-semibold transition
              ${status === "success"
                ? "bg-emerald-600 text-white"
                : "bg-indigo-600 hover:bg-indigo-500 text-white disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed"
              }
            `}
          >
            {status === "checking" && (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Vérification...
              </span>
            )}
            {status === "success" && "Terminal déverrouillé"}
            {status === "idle" && "Déverrouiller"}
          </button>
        </form>

        <div className="text-center text-xs text-slate-500">
          Entrez l'ID badge de l'employé présent durant la nuit
        </div>
      </div>
    </div>
  );
}